/**
 * @file MoveLotBinDialog.tsx
 * @description Размещение и перестановка партии между ячейками (phase19.14,
 * W14; скан места и режим «Разместить» — phase19.24 этап 1, шаг 4; скан
 * ячейки/полки/стеллажа общим `BinScanField` — phase19.22, W14-9).
 *
 * Это НЕ складской документ в привычном смысле: количество и склад не
 * меняются, меняется только адрес хранения (`Lot.binId`). Поэтому диалог
 * короткий — отсканировать или выбрать ячейку, либо снять размещение.
 *
 * Партия без адреса и партия с адресом — одна и та же форма с разными
 * словами: «Разместить» против «Переставить». Состояние берём из
 * `currentBinCode`, отдельного пропа-режима нет — иначе вызывающие экраны
 * начали бы передавать его вразнобой.
 *
 * Ячейки грузятся по складу партии: ячейка чужого склада означала бы переезд
 * партии без документа перемещения, сервер такое отклоняет
 * (422 `bin_warehouse_mismatch`).
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useNotify } from "../ui/NotifyProvider";
import { BinScanField } from "./bins/BinScanField";
import { PLACEMENT_TEXT } from "./stock/stock-placement-texts";
import { binsApi, type StorageBinDto } from "../../lib/api-bins";
import { ApiError } from "../../lib/api-client";

interface Props {
  lotId: string;
  batchNumber: string;
  warehouseId: string;
  /** Текущий адрес — подпись «сейчас лежит в …»; `null` — партия без места. */
  currentBinCode: string | null;
  onMoved: () => void;
  onClose: () => void;
}

export function MoveLotBinDialog({
  lotId,
  batchNumber,
  warehouseId,
  currentBinCode,
  onMoved,
  onClose,
}: Props) {
  const notify = useNotify();
  const [bins, setBins] = useState<StorageBinDto[]>([]);
  const [binId, setBinId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Партия ещё нигде не лежит — это размещение, а не перестановка. */
  const placing = !currentBinCode;

  useEffect(() => {
    let cancelled = false;
    binsApi
      // Выбор ячейки: сводка содержимого не нужна (phase19.23 этап A).
      .list({ warehouseId, withSummary: false })
      .then((data) => {
        if (!cancelled) setBins(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : PLACEMENT_TEXT.binsLoadError,
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [warehouseId]);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const result = await binsApi.moveLot(lotId, binId || null);
      // Перестановка оформляется документом (phase19.23 этап B) — показываем
      // его номер: по нему кладовщик найдёт операцию в реестре перемещений.
      // Номера нет, если партия уже лежала там, куда её просили положить.
      const doc = result.transferNumber ? ` · документ ${result.transferNumber}` : "";
      const bin = result.binCode ?? "ячейку";
      notify.success(
        binId
          ? `${placing ? PLACEMENT_TEXT.placedOne(batchNumber, bin) : `Партия ${batchNumber} перемещена в ${bin}`}${doc}`
          : `Размещение партии ${batchNumber} снято${doc}`,
      );
      onMoved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось переставить");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* fullScreen="lg": планшет кладовщика работает по мобильным правилам. */}
      <DialogContent fullScreen="lg" className="lg:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {placing ? "Разместить партию" : "Переставить партию"} {batchNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <p className="text-sm text-on-surface-variant">
            Сейчас: {currentBinCode ?? "размещение не задано"}. Остаток и склад
            не меняются — это только адрес хранения.
          </p>
          {/* Не `<label>`: внутри поля плитки уровня и кнопка сброса. */}
          <div className="flex flex-col gap-1">
            <span className="label-editorial">{placing ? "Ячейка" : "Новая ячейка"}</span>
            <BinScanField
              warehouseId={warehouseId}
              bins={bins}
              value={binId}
              onChange={setBinId}
              emptyOptionLabel={placing ? undefined : "ячейка не выбрана — кнопка снимет размещение"}
              disabled={loading || saving}
            />
          </div>
          {!loading && bins.length === 0 ? (
            <span className="text-xs text-on-surface-variant">
              {PLACEMENT_TEXT.noBins}
            </span>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {PLACEMENT_TEXT.cancel}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={saving || loading}
          >
            {saving ? "Сохраняем…" : placing ? "Разместить" : "Переставить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
